import { Search } from 'lucide-react'
import { useId, useState, type KeyboardEvent } from 'react'

import { hiddenText, highlightParts, pickerOptions } from '../model/select'
import type { CashierPerson } from '../model/types'
import './schedule.css'

export interface CashierSearchProps {
  people: readonly CashierPerson[]
  /** Уже выбранные: в выдачу не попадают. */
  excluded: readonly string[]
  recent: readonly string[]
  /** Название строки поиска для диктора и надпись в ней. */
  label?: string
  /** Уже выбранный кассир: его имя стоит внутри поля, поиск продолжается справа от него. */
  current?: CashierPerson | null
  /** Подсказка при наведении на поле (например, откуда взят выбранный кассир). */
  title?: string | undefined
  onPick: (cashierId: string) => void
}

/**
 * Строка поиска кассира с выпадающими подсказками. Пока в строке пусто, при фокусе под ней показаны пять подсказок: последние
 * выбранные, а недостающее добрано первыми кассирами справочника по алфавиту; с текстом — совпадения по имени с подсветкой
 * найденного (не больше восьми, остальное — «ещё N — уточните запрос»). Нажатие на строку выбирает кассира. Список подсказок лежит ПОВЕРХ страницы (не сдвигает таблицу) и закрывается Esc или щелчком мимо. Сколько бы кассиров ни было, выдача короткая: так поиск одинаково работает при четырёх и при четырёхстах.
 */
export function CashierSearch({ people, excluded, recent, label = 'Найти кассира', current = null, title, onPick }: CashierSearchProps) {
  const baseId = useId()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  const options = pickerOptions(people, excluded, recent, query)
  const hidden = hiddenText(options.hidden)
  const expanded = open
  const listId = `${baseId}-list`

  const pick = (person: CashierPerson) => {
    onPick(person.id)
    setQuery('')
    setActive(-1)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive(Math.min(active + 1, options.items.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(Math.max(active - 1, 0))
    } else if (event.key === 'Enter') {
      // Enter выбирает строку и не отправляет форму вокруг.
      event.preventDefault()
      const person = options.items[Math.max(active, 0)]
      if (expanded && person !== undefined) pick(person)
    } else if (event.key === 'Escape' && open) {
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <div
      className="sch-select__search"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false)
      }}
    >
      <label className="sch-select__field" title={title} data-open={expanded || undefined}>
        {current !== null && <span className="sch-tag">{current.name}</span>}
        <input
          type="text"
          role="combobox"
          className="sch-select__input"
          aria-label={label}
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={expanded && active >= 0 ? `${baseId}-option-${active}` : undefined}
          autoComplete="off"
          placeholder={current === null ? label : 'Выбрать другого'}
          value={query}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onKeyDown={onKeyDown}
        />
        <Search size={16} aria-hidden="true" className="sch-select__icon" />
      </label>
      {expanded && (
        <div className="sch-select__drop" onMouseDown={(event) => event.preventDefault()}>
          <ul id={listId} role="listbox" className="sch-select__options" aria-label={options.mode === 'suggest' ? 'Подсказки кассиров' : 'Найденные кассиры'}>
            {options.items.map((person, index) => {
              const recentRow = index < options.recentCount
              return (
                <li
                  key={person.id}
                  id={`${baseId}-option-${index}`}
                  role="option"
                  aria-selected={index === active}
                  className="sch-option"
                  data-active={index === active || undefined}
                  data-recent={recentRow || undefined}
                  onClick={() => pick(person)}
                >
                  <span className="sch-option__name">
                    {highlightParts(person.name, options.mode === 'search' ? query : '').map((part, partIndex) =>
                      part.matched ? <mark key={partIndex}>{part.text}</mark> : <span key={partIndex}>{part.text}</span>,
                    )}
                  </span>
                  {recentRow && <span className="sch-option__recent">недавно</span>}
                </li>
              )
            })}
          </ul>
          {options.items.length === 0 && (
            <p className="sch-select__status" role="status">
              {options.mode === 'search' ? 'Никого не нашли: проверьте написание имени.' : 'В справочнике пока нет кассиров.'}
            </p>
          )}
          {hidden !== null && (
            <p className="sch-select__status" role="status">
              {hidden}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
